// Striscia promemoria della sostituzione ancora da spedire: piena larghezza,
// sotto la testata, sopra le colonne.
//
// ⚠️ MAI collassabile e mai dentro una sezione: Davide si segna la sostituzione
// mentre è al telefono col cliente e la deve ritrovare aprendo l'ordine, non
// aprendo una piega. Un promemoria che dipende da una sezione aperta non è un
// promemoria. Sulla stragrande maggioranza degli ordini non esiste affatto —
// nessuno spazio occupato, nessun bordo vuoto da ignorare.
import { useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { togliIndirizzoSostituzione } from "../../api";
import { statoSostituzione } from "../../sostituzione";
import type { Order } from "../../types";
import { IconSwap } from "../icons";
import { BTN } from "./stile";

interface PromemoriaSostituzioneProps {
  order: Order;
  /** Apre il dialog di emissione (resi): è lì che nasce l'etichetta. */
  onCreaEtichetta: () => void;
  /** Ricarica l'ordine dopo «Togli». */
  onChanged: () => void;
  /** Toast della pagina — regola Davide: ogni pulsante dà un esito. */
  onMessage: (testo: string) => void;
  onError: (testo: string | null) => void;
}

export function PromemoriaSostituzione({
  order,
  onCreaEtichetta,
  onChanged,
  onMessage,
  onError,
}: PromemoriaSostituzioneProps) {
  const [busy, setBusy] = useState(false);
  const indirizzo = order.replacementAddress;
  const { presente, diverso, giorniAperta, inRitardo } = statoSostituzione(order);
  // `replacementPending` = indirizzo registrato e NESSUNA sostituzione aperta
  // nei resi: è l'unico segnale già in mano al browser che dice «l'etichetta
  // non c'è». Non lo si deduce da `label_path`.
  if (!presente || !indirizzo || order.replacementPending !== true) return null;

  async function togli() {
    if (busy) return;
    // Indirizzo e nota si perdono insieme, e la nota è quello che il cliente ha
    // dettato al telefono: non si cancella con un click solo.
    if (
      !window.confirm(
        "Togliere la sostituzione da questo ordine? Indirizzo e nota del cliente si perdono.",
      )
    ) {
      return;
    }
    setBusy(true);
    onError(null);
    try {
      await togliIndirizzoSostituzione(order.id);
      onMessage("Sostituzione tolta dall'ordine.");
      onChanged();
    } catch (err) {
      onError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  const destinatario =
    [indirizzo.name, indirizzo.surname].filter(Boolean).join(" ") || indirizzo.company;
  const dove = [destinatario, indirizzo.city || indirizzo.address].filter(Boolean).join(" — ");
  const quando =
    giorniAperta === null
      ? null
      : giorniAperta === 0
        ? "aperta oggi"
        : `aperta da ${giorniAperta} ${giorniAperta === 1 ? "giorno" : "giorni"}`;

  return (
    <div
      data-testid="promemoria-sostituzione"
      className={[
        "flex flex-wrap items-center gap-x-4 gap-y-2.5 rounded-xl border border-l-[3px] px-4 py-3",
        inRitardo
          ? "border-red/50 border-l-red bg-red-soft"
          : "border-amber/50 border-l-amber bg-amber-soft",
      ].join(" ")}
    >
      <IconSwap
        className={`mt-0.5 h-[18px] w-[18px] shrink-0 fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round] ${
          inRitardo ? "text-red" : "text-amber"
        }`}
      />
      <div className="min-w-0 flex-1 text-[13px] leading-normal">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-[15px] font-bold text-ink">Sostituzione da spedire</span>
          {quando && (
            <span
              className={inRitardo ? "text-[13px] font-semibold text-red" : "text-[13px] text-ink"}
            >
              {quando}
            </span>
          )}
        </div>
        {order.replacementNote && <div className="mt-0.5 text-ink">{order.replacementNote}</div>}
        {/* Frase intera in UN solo nodo di testo (stessa ragione della fascia
            cliente): spezzarla per grassettare «non» la rende illeggibile a
            chi cerca la riga — RTL compreso. L'enfasi la porta la riga. */}
        <div className={diverso ? "mt-0.5 font-semibold text-ink" : "mt-0.5 text-ink"}>
          {diverso
            ? `Va a ${dove} — non all'indirizzo dell'ordine.`
            : "Va allo stesso indirizzo dell'ordine."}
        </div>
        <div className="mt-0.5 text-[12.5px] text-ink">
          Etichetta non ancora emessa: si aspettano colli e misure dal pronto merce del fornitore.
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <button type="button" onClick={onCreaEtichetta} className={BTN}>
          Crea etichetta
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void togli()}
          className={`${BTN} hover:!border-red hover:!text-red`}
        >
          {busy ? "Tolgo…" : "Togli"}
        </button>
      </div>
    </div>
  );
}
